import { memo, useId, type ReactElement } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { PrimaryFoodTypeBadge } from "@/components/menu/PrimaryFoodTypeBadge";
import { designTokens } from "@/theme";
import { getDesignTokens } from "@/theme/designTokens";
import type { MenuItemTagV2 } from "@/models/menu-item-v2";
import {
  getComplianceContainsAllergenBadges,
  getOptionalDietaryChips,
  hasMenuItemMetadata,
} from "@/utils/compliance-tags";
import { getPrimaryFoodType } from "@/utils/tag-dietary";
import { REACT_STABLE_KEY_TYPE, buildStableKey, opaqueIdFingerprint } from "@/utils/react-key.util";

export type MenuItemMetadataProps = {
  tags?: MenuItemTagV2[];
};

const KITCHEN_CROSS_CONTACT_NOTE =
  "Kitchen handles common allergens. Cross-contact may occur.";

function normalizeAllergenDisplayLabel(label: string): string {
  return label.replace(/^contains\s+/i, "").trim();
}

function formatAllergenInlineList(labels: string[]): string {
  return labels.map(normalizeAllergenDisplayLabel).filter(Boolean).join(" \u2022 ");
}

function buildAllergenAriaLabel(labels: string[]): string {
  const normalized = labels.map(normalizeAllergenDisplayLabel).filter(Boolean);
  if (normalized.length === 0) return "";
  if (normalized.length === 1) return `Contains ${normalized[0]}`;
  const last = normalized[normalized.length - 1];
  const rest = normalized.slice(0, -1).join(", ");
  return `Contains ${rest}, and ${last}`;
}

function KitchenDisclaimerIcon(): ReactElement {
  const theme = useTheme();

  return (
    <Box
      component="span"
      aria-hidden="true"
      data-testid="kitchen-allergen-disclaimer-icon"
      sx={{
        flexShrink: 0,
        width: 14,
        height: 14,
        mt: "1px",
        color: theme.palette.text.disabled,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        focusable="false"
      >
        <path
          d="M12 2.5 5.5 5.25v5.75c0 4.55 2.95 8.8 6.5 9.75 3.55-.95 6.5-5.2 6.5-9.75V5.25L12 2.5Z"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinejoin="round"
        />
      </svg>
    </Box>
  );
}

type MetadataChipProps = {
  label: string;
  ariaLabel: string;
};

function MetadataChip({ label, ariaLabel }: MetadataChipProps): ReactElement {
  const theme = useTheme();
  const meta = getDesignTokens(theme).metadataInline;

  return (
    <Box
      component="span"
      role="listitem"
      aria-label={ariaLabel}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        maxWidth: "100%",
        color: meta.color,
        fontSize: theme.typography.pxToRem(meta.fontSizePx.default),
        fontWeight: meta.fontWeight,
        lineHeight: 1.25,
        background: "none",
        border: "none",
        boxShadow: "none",
      }}
    >
      {label}
    </Box>
  );
}

/**
 * Premium stacked metadata for menu item detail — food identity, allergens, optional dietary chips.
 */
export const MenuItemMetadata = memo(function MenuItemMetadata({
  tags,
}: MenuItemMetadataProps): ReactElement | null {
  const theme = useTheme();
  const sectionId = useId();

  if (!hasMenuItemMetadata(tags)) return null;

  const primaryFoodType = getPrimaryFoodType(tags);
  const containsAllergens = getComplianceContainsAllergenBadges(tags);
  const optionalDietaryChips = getOptionalDietaryChips(tags);
  const allergenLabels = containsAllergens.map((badge) => badge.label);
  const showKitchenNote = containsAllergens.length > 0;

  return (
    <Box
      component="section"
      id={sectionId}
      aria-label="Food type and allergen information"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: designTokens.spacing.sm / 8,
        width: "100%",
      }}
    >
      {primaryFoodType ? <PrimaryFoodTypeBadge type={primaryFoodType} /> : null}

      {containsAllergens.length > 0 ? (
        <Box
          role="note"
          aria-label={buildAllergenAriaLabel(allergenLabels)}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 0.75,
            width: "100%",
            minWidth: 0,
          }}
        >
          <Typography
            component="span"
            aria-hidden="true"
            sx={{
              flexShrink: 0,
              fontSize: theme.typography.pxToRem(14),
              lineHeight: 1.45,
              color: theme.palette.warning.main,
            }}
          >
            &#9888;
          </Typography>
          <Typography
            component="p"
            variant="body2"
            sx={{
              m: 0,
              minWidth: 0,
              fontSize: theme.typography.pxToRem(13),
              lineHeight: 1.45,
              color: theme.palette.text.secondary,
              wordBreak: "break-word",
            }}
          >
            <Box
              component="span"
              sx={{ fontWeight: theme.typography.fontWeightMedium, color: theme.palette.text.primary }}
            >
              Contains:
            </Box>{" "}
            {formatAllergenInlineList(allergenLabels)}
          </Typography>
        </Box>
      ) : null}

      {optionalDietaryChips.length > 0 ? (
        <Box
          component="ul"
          role="list"
          aria-label="Additional dietary information"
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 0.75,
            m: 0,
            p: 0,
            listStyle: "none",
            width: "100%",
          }}
        >
          {optionalDietaryChips.map((chip, chipIdx) => (
            <MetadataChip
              key={buildStableKey(
                REACT_STABLE_KEY_TYPE.tagChip,
                chipIdx,
                opaqueIdFingerprint(chip.id),
                chip.code
              )}
              label={chip.label}
              ariaLabel={chip.ariaLabel}
            />
          ))}
        </Box>
      ) : null}

      {showKitchenNote ? (
        <Box
          role="note"
          aria-label={KITCHEN_CROSS_CONTACT_NOTE}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 0.75,
            width: "100%",
            minWidth: 0,
          }}
        >
          <KitchenDisclaimerIcon />
          <Typography
            component="p"
            variant="caption"
            sx={{
              m: 0,
              minWidth: 0,
              color: theme.palette.text.disabled,
              fontSize: theme.typography.pxToRem(11),
              lineHeight: 1.45,
              wordBreak: "break-word",
            }}
          >
            {KITCHEN_CROSS_CONTACT_NOTE}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
});
